<template>
	<SignUpPageBase :content="this" max-width="80%">
		<h4 class="font_lg font_bold margin_title_bottom">{{ $t("signUp.overview.title") }}</h4>
		<table class="overviewTable">
			<!-- Master User -->
			<tr>
				<th>{{ $t("signUp.overview.masterUser")}}</th>
				<td></td>
			</tr>
			<tr>
				<td>{{ $t("common.name") }}:</td>
				<td> {{ info.userFirstName + " " + info.userLastName }}</td>
			</tr>
			<tr>
				<td>{{ $t("common.email") }}:</td>
				<td>{{ info.userEmail }}</td>
			</tr>
			<tr>
				<td>{{ $t("common.phone") }}:</td>
				<td>{{ info.userPhone }}</td>
			</tr>
			<!-- Company Data -->
			<tr>
				<th>{{ $t("signUp.overview.company")}}</th>
				<td></td>
			</tr>
			<tr>
				<td>{{ $t("common.country") }}:</td>
				<td>{{ info.companyCountry }}</td>
			</tr>
			<tr>
				<td>{{ $t("common.companyName") }}:</td>
				<td>{{ info.companyName }}</td>
			</tr>
			<tr>
				<td>{{ $t("common.registerAuthority") }}:</td>
				<td>{{ info.companyRegisterCourtName }}</td>
			</tr>
			<tr v-if="info.companyRegistrationNumber">
				<td>{{ $t("common.registrationNumber") }}:</td>
				<td>{{ info.companyRegistrationNumber }}</td>
			</tr>
			<tr>
				<td>{{ $t("common.vatId") }}:</td>
				<td>{{ info.companyVatNumber }}</td>
			</tr>
			<!-- Bank Account -->
			<tr>
				<th>{{ $t("signUp.overview.bankAccounts")}}</th>
				<td></td>
			</tr>
			<tr>
				<td>{{ $t("common.iban") }}:</td>
				<td>{{ info.bankAccountIban }}</td>
			</tr>
			<tr>
				<td>{{ $t("common.bic") }}:</td>
				<td>{{ info.bankAccountBic }}</td>
			</tr>
			<tr>
				<td>{{ $t("common.currency") }}:</td>
				<td>{{ info.bankAccountCurrency }}</td>
			</tr>
			<!-- Representatives -->
			<template v-if="info.representatives.length > 0">
				<tr>
					<th>{{ $t("signUp.overview.representatives")}}</th>
					<td></td>
				</tr>
				<template v-for="(rep, index) in info.representatives">
					<tr>
						<td>{{ $t("common.name") }}:</td>
						<td>{{ rep.firstName + " " + rep.lastName}}</td>
					</tr>
					<tr>
						<td>{{ $t("common.email") }}:</td>
						<td>{{ rep.email }}</td>
					</tr>
					<tr>
						<td>{{ $t("common.dateOfBirth") }}:</td>
						<td>{{ rep.birthday }}</td>
					</tr>
					<tr>
						<td>{{ $t("common.language") }}:</td>
						<td>{{ rep.language }}</td>
					</tr>
					<tr class="break" v-if="index !== info.representatives.length - 1"/>
				</template>
			</template>
			<!-- Beneficial Owners -->
			<template v-if="info.beneficialOwners.length > 0">
				<tr>
					<th>{{ $t("signUp.overview.beneficialOwners")}}</th>
					<td></td>
				</tr>
				<template v-for="(owner, index) in info.beneficialOwners">
					<tr>
						<td>{{ $t("common.name") }}:</td>
						<td>{{ owner.firstName + " " + owner.lastName}}</td>
					</tr>
					<tr>
						<td>{{ $t("common.dateOfBirth") }}:</td>
						<td>{{ owner.birthday }}</td>
					</tr>
					<tr>
						<td>{{ $t("common.placeOfBirth") }}:</td>
						<td>{{ owner.birthplace }}</td>
					</tr>
					<tr>
						<td>{{ $t("common.nationality") }}:</td>
						<td>{{ owner.nationality }}</td>
					</tr>
					<tr>
						<td>{{ $t("common.address") }}:</td>
						<td>{{ owner.address }}</td>
					</tr>
					<tr class="break" v-if="index !== info.beneficialOwners.length - 1"/>
				</template>
			</template>
		</table>
	</SignUpPageBase>
</template>

<script>

import SignUpPageBase from "@/views/register/SignUpPageBase";
import {mapGetters} from "vuex";
import {commonApi} from "@/common/api/commonApi";
import {formatAsAddress, formatAsCountryName, formatAsLocalizedDate, formatAsLocalizedEnum} from "@/common/format";
import {enumDef} from "@/common/enumDefs";

export default {
	components: { SignUpPageBase },
	data() {
		return {
			info: {},
			registerCourts: []
		};
	},
	created() {
		this.info = {
			...this.signUpData,
			companyCountry: formatAsCountryName(this.signUpData.companyCountry),
			companyRegisterCourtName: "", // Setup requires loading translations from backend
		};

		this.info.representatives = this.signUpData.representatives.map(rep => {
			return {
				...rep,
				birthday: formatAsLocalizedDate(rep.birthday, { valueFormat: "dd.MM.yyyy" }),
				language: formatAsLocalizedEnum(enumDef.language, rep.language)
			}
		});

		this.info.beneficialOwners = this.signUpData.beneficialOwners.map(owner => {
			return {
				...owner,
				birthday: formatAsLocalizedDate(owner.birthday, { valueFormat: "dd.MM.yyyy" }),
				nationality: formatAsCountryName(owner.nationality),
				address: formatAsAddress(
					owner.residentialAddress.street,
					owner.residentialAddress.houseNumber,
					owner.residentialAddress.postCode,
					owner.residentialAddress.city,
					owner.residentialAddress.country
				)
			};
		});

		this.setupRegisterCourtById(this.info, this.signUpData.companyCountry, this.signUpData.companyRegisterCourtId);
	},
	computed: {
		...mapGetters({
			signUpData: "local/signUpData",
			signUpCredentials: "signUpCredentials",
			signUpSettings: "local/signUpSettings",
		}),
	},
	methods: {
		setupRegisterCourtById(info, country, courtId) {
			commonApi.Registercourts(country)
				.then(res => {
					let registerForId = res.find(item => item.id === courtId);
					info.companyRegisterCourtName = this.$t(registerForId.translationKey);
				});
		}
	}
};
</script>

<style lang="scss" scoped>
	@import "@/assets/scss/base_variables.scss";

	.overviewTable {
		tr.break {
			height: 1rem;
		}

		tr > td,th {
			min-width: 100px;
			vertical-align: top;
			padding-right: 2rem;
			text-align: right;
		}

		tr > th {
			font-size: $font-larger;
			padding-top: 2rem;
			padding-bottom: 1rem;
		}

		tr > td:nth-child(2) {
			padding-right: 0;
			text-align: left;
			font-weight: $font-weight-bold;
		}
	}
</style>